import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Package, Plus, LayoutDashboard, MapPin, CreditCard, Download, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '@/components/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);

  useEffect(() => { fetchQuotes(); fetchOrders(); }, []);

  const fetchQuotes = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/quotes`, { withCredentials: true }); setQuotes(data); } catch (e) {}
  };
  const fetchOrders = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/orders`, { withCredentials: true }); setOrders(data); } catch (e) {}
  };

  const handleCreateOrder = async () => {
    if (!deliveryAddress) { toast.error('Please enter delivery address'); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/orders`, {
        quote_id: selectedQuote.id, delivery_address: deliveryAddress, delivery_date: deliveryDate || null
      }, { withCredentials: true });
      toast.success('Order created!');
      setOrderDialogOpen(false); setDeliveryAddress(''); setDeliveryDate('');
      fetchQuotes(); fetchOrders();
    } catch (e) { toast.error('Failed to create order'); }
    finally { setLoading(false); }
  };

  const getStatusBadge = (status) => {
    const v = {
      pending: 'bg-yellow-100 text-yellow-800', ordered: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800', completed: 'bg-slate-100 text-slate-800',
      in_production: 'bg-orange-100 text-orange-800', quality_check: 'bg-purple-100 text-purple-800',
      ready_for_delivery: 'bg-teal-100 text-teal-800', out_for_delivery: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-emerald-100 text-emerald-800'
    };
    return <Badge className={`${v[status] || v.pending} rounded-sm text-[10px] sm:text-xs`} data-testid={`status-${status}`}>{status.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AppHeader title="Hi Tech Industries">
        <Button variant="ghost" size="sm" onClick={() => navigate('/configurator')} className="text-slate-600" data-testid="new-quote-button">
          <Plus size={18} className="mr-2" /> New Quote
        </Button>
        {user?.role === 'admin' && (
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-slate-600" data-testid="admin-dashboard-button">
            <LayoutDashboard size={18} className="mr-2" /> Admin
          </Button>
        )}
      </AppHeader>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-1">My Dashboard</h2>
          <p className="text-sm sm:text-base text-slate-500">Manage your quotes and orders</p>
        </div>

        <Tabs defaultValue="quotes" className="space-y-4 sm:space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-sm w-full sm:w-auto">
            <TabsTrigger value="quotes" className="data-[state=active]:bg-slate-100 rounded-sm flex-1 sm:flex-initial text-xs sm:text-sm" data-testid="quotes-tab">
              <FileText size={16} className="mr-1.5 sm:mr-2" /> Quotes ({quotes.length})
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-slate-100 rounded-sm flex-1 sm:flex-initial text-xs sm:text-sm" data-testid="orders-tab">
              <Package size={16} className="mr-1.5 sm:mr-2" /> Orders ({orders.length})
            </TabsTrigger>
          </TabsList>

          {/* Quotes */}
          <TabsContent value="quotes" className="space-y-3 sm:space-y-4">
            {quotes.length === 0 ? (
              <Card className="border-slate-200 rounded-sm shadow-none">
                <CardContent className="py-10 sm:py-12 text-center">
                  <FileText size={40} className="mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500 mb-4 text-sm sm:text-base">No quotes yet</p>
                  <Button onClick={() => navigate('/configurator')} className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="create-first-quote">Create Quote</Button>
                </CardContent>
              </Card>
            ) : quotes.map((quote) => (
              <Card key={quote.id} className="border border-slate-200 rounded-sm shadow-none" data-testid={`quote-card-${quote.id}`}>
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base sm:text-xl font-medium truncate">{quote.product?.name || 'Product'}</CardTitle>
                      <CardDescription className="mt-0.5 text-xs sm:text-sm">{quote.product?.style} | {quote.product?.material}</CardDescription>
                    </div>
                    {getStatusBadge(quote.status)}
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0 space-y-3 sm:space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-slate-500">Size</span>
                      <p className="text-slate-900 font-medium mt-0.5 font-mono text-xs sm:text-sm">{quote.width}x{quote.height} ft</p>
                    </div>
                    <div>
                      <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-slate-500">Area</span>
                      <p className="text-slate-900 font-medium mt-0.5 font-mono text-xs sm:text-sm">{quote.area.toFixed(1)} sqft</p>
                    </div>
                    <div>
                      <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-slate-500">Qty</span>
                      <p className="text-slate-900 font-medium mt-0.5 font-mono text-xs sm:text-sm">{quote.quantity}</p>
                    </div>
                    <div>
                      <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-slate-500">Total</span>
                      <p className="text-[#0052FF] font-medium text-sm sm:text-lg mt-0.5 font-mono">&#8377;{quote.total_price.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {quote.status === 'pending' && (
                      <Dialog open={orderDialogOpen && selectedQuote?.id === quote.id} onOpenChange={(open) => { setOrderDialogOpen(open); if (open) setSelectedQuote(quote); }}>
                        <DialogTrigger asChild>
                          <Button size="sm" className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm h-8 sm:h-9 text-xs sm:text-sm" data-testid={`place-order-button-${quote.id}`}>Place Order</Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm max-w-[95vw] sm:max-w-lg" data-testid="order-dialog">
                          <DialogHeader>
                            <DialogTitle>Create Order</DialogTitle>
                            <DialogDescription>Enter delivery details</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-3">
                            <div>
                              <Label className="text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Delivery Address *</Label>
                              <Input value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="123 Main St, City" className="mt-1.5 rounded-sm" data-testid="delivery-address-input" />
                            </div>
                            <div>
                              <Label className="text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Delivery Date</Label>
                              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="mt-1.5 rounded-sm" data-testid="delivery-date-input" />
                            </div>
                            <Button onClick={handleCreateOrder} disabled={loading} className="w-full bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="confirm-order-button">
                              {loading ? 'Creating...' : 'Confirm Order'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    <Button variant="outline" size="sm" onClick={() => {
                      const msg = `*Hi Tech Industries - Quote*%0A%0AProduct: ${quote.product?.name}%0AStyle: ${quote.product?.style} | ${quote.product?.material}%0ADimensions: ${quote.width} x ${quote.height} ft%0AArea: ${quote.area.toFixed(1)} sq ft%0AQuantity: ${quote.quantity}%0A%0A*Total: ₹${quote.total_price.toFixed(2)}*%0A%0APh: 6006612592, 6006612593%0AGSTIN: 01BLOPS3940K1ZT`;
                      window.open(`https://wa.me/?text=${msg}`, '_blank');
                    }} className="rounded-sm h-8 sm:h-9 text-xs sm:text-sm text-green-700 border-green-200 hover:bg-green-50" data-testid={`whatsapp-quote-${quote.id}`}>
                      <Share2 size={14} className="mr-1.5" /> WhatsApp
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders" className="space-y-3 sm:space-y-4">
            {orders.length === 0 ? (
              <Card className="border-slate-200 rounded-sm shadow-none">
                <CardContent className="py-10 sm:py-12 text-center">
                  <Package size={40} className="mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500 text-sm sm:text-base">No orders yet</p>
                </CardContent>
              </Card>
            ) : orders.map((order) => (
              <Card key={order.id} className="border border-slate-200 rounded-sm shadow-none" data-testid={`order-card-${order.id}`}>
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base sm:text-xl font-medium truncate">{order.quote?.product?.name || 'Product'}</CardTitle>
                      <CardDescription className="mt-0.5 text-xs">ID: {order.id.slice(0, 8)}...</CardDescription>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {getStatusBadge(order.status)}
                      {order.payment_status === 'paid' && <Badge className="bg-green-100 text-green-800 rounded-sm text-[10px]" data-testid={`paid-badge-${order.id}`}>Paid</Badge>}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-slate-500">Delivery</span>
                      <p className="text-slate-900 mt-0.5 text-xs sm:text-sm truncate">{order.delivery_address}</p>
                    </div>
                    <div>
                      <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-slate-500">Amount</span>
                      <p className="text-[#0052FF] font-medium text-sm sm:text-lg mt-0.5 font-mono">&#8377;{order.quote?.total_price?.toFixed(2)}</p>
                    </div>
                  </div>
                  {/* Action buttons - stack on mobile */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" onClick={() => navigate(`/track/${order.id}`)} className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm h-8 sm:h-9 text-xs sm:text-sm" data-testid={`track-order-button-${order.id}`}>
                      <MapPin size={14} className="mr-1.5" /> Track
                    </Button>
                    {order.payment_status !== 'paid' && (
                      <Button variant="outline" size="sm" onClick={async () => {
                        try {
                          const { data } = await axios.post(`${BACKEND_URL}/api/payments/create-checkout`, { order_id: order.id, origin_url: window.location.origin }, { withCredentials: true });
                          window.location.href = data.url;
                        } catch (e) { toast.error('Failed to initiate payment'); }
                      }} className="rounded-sm h-8 sm:h-9 text-xs sm:text-sm border-green-200 text-green-700" data-testid={`pay-order-button-${order.id}`}>
                        <CreditCard size={14} className="mr-1.5" /> Pay
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => window.open(`${BACKEND_URL}/api/orders/${order.id}/invoice`, '_blank')} className="rounded-sm h-8 sm:h-9 text-xs sm:text-sm" data-testid={`download-invoice-button-${order.id}`}>
                      <Download size={14} className="mr-1.5" /> Invoice
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => {
                      const msg = `*Hi Tech Industries - Order*%0A%0AOrder: #${order.id.slice(0, 8)}%0AProduct: ${order.quote?.product?.name}%0ADimensions: ${order.quote?.width} x ${order.quote?.height} ft%0AStatus: ${order.status.replace('_', ' ')}%0ADelivery: ${order.delivery_address}%0A%0A*Amount: ₹${order.quote?.total_price?.toFixed(2)}*%0A%0APh: 6006612592, 6006612593%0AGSTIN: 01BLOPS3940K1ZT`;
                      window.open(`https://wa.me/?text=${msg}`, '_blank');
                    }} className="rounded-sm h-8 sm:h-9 text-xs sm:text-sm text-green-700 border-green-200 hover:bg-green-50" data-testid={`whatsapp-order-${order.id}`}>
                      <Share2 size={14} className="mr-1.5" /> WhatsApp
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
